import { useEffect, useRef, useState } from 'react';
import { listLights, setLight, type Light } from '../lib/home.js';
import { useLoad } from '../lifeos/useLoad.js';
import { useToast } from '../lifeos/toast.js';
import { Group, Row } from '../ui/index.js';

const PRESETS = [10, 40, 70, 100] as const;
const SEND_DELAY_MS = 250;

/** Lights from Home Assistant: on/off, a brightness slider and presets. Renders nothing when HA isn't set up. */
export default function Lights(): JSX.Element | null {
  const state = useLoad(() => listLights(), []);
  const lights = state.data?.configured ? state.data.lights : [];
  if (lights.length === 0) return null;
  return (
    <Group header="Lights" id="home-lights">
      {lights.map((l) => (
        <LightRow key={l.id} initial={l} />
      ))}
    </Group>
  );
}

function LightRow({ initial }: { initial: Light }): JSX.Element {
  const [light, setLocal] = useState(initial);
  // The slider moves freely; only the last position (after a short pause) goes to HA.
  const [level, setLevel] = useState(initial.brightness ?? 50);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Only the newest change's answer may update the row; an older one is stale.
  const seq = useRef(0);
  const toast = useToast();
  useEffect(() => () => clearTimeout(timer.current), []);

  async function send(want: { on: boolean; brightness?: number }): Promise<void> {
    const before = light;
    setLocal({ ...light, on: want.on, brightness: want.on ? (want.brightness ?? light.brightness) : null });
    const mine = ++seq.current;
    try {
      const next = await setLight(light.id, want);
      if (mine !== seq.current) return;
      setLocal(next);
      if (next.brightness !== null) setLevel(next.brightness);
    } catch {
      if (mine !== seq.current) return;
      setLocal(before);
      toast.error(`Couldn’t change ${light.name}`);
    }
  }

  function slide(v: number): void {
    setLevel(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void send({ on: true, brightness: v }), SEND_DELAY_MS);
  }

  return (
    <Row
      className="lt-row"
      tone={light.available ? 'default' : 'muted'}
      title={light.name}
      detail={!light.available ? 'offline' : light.on ? (light.brightness !== null ? `${light.brightness}%` : 'On') : 'Off'}
      sub={
        light.available && light.dimmable ? (
          <>
            <input
              className="lt-slider"
              type="range"
              min={1}
              max={100}
              value={light.on ? level : 1}
              aria-label={`${light.name} brightness`}
              onChange={(e) => slide(Number(e.target.value))}
            />
            <span className="lt-presets">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`lt-preset${light.on && light.brightness !== null && Math.abs(light.brightness - p) <= 2 ? ' lt-preset--on' : ''}`}
                  onClick={() => {
                    setLevel(p);
                    void send({ on: true, brightness: p });
                  }}
                >
                  {p}%
                </button>
              ))}
            </span>
          </>
        ) : undefined
      }
      trailing={
        <label className="pj-switch" title={light.on ? 'On' : 'Off'}>
          <input
            type="checkbox"
            role="switch"
            checked={light.on}
            disabled={!light.available}
            aria-label={`${light.name} on`}
            onChange={(e) => void send({ on: e.target.checked })}
          />
          <span aria-hidden="true" />
        </label>
      }
    />
  );
}
